/**
 * Public Share — Bespoke / Personal Audio
 *
 * Unauthenticated landing. Stable URL pattern:
 *   /share/audio/:slug    (e.g. /share/audio/sam-night-3hr)
 *
 * Recipient lands here, listens without signing in. Member texts the
 * link → this page renders.
 *
 * Mar 5 2026 · Brand-aware skin. The backend's info response now
 * includes a `brand` field ("soulumo" for Soulumo installations,
 * "reclaim" otherwise). We render two completely separate aesthetics
 * so a Soulumo overnight never feels like a Reclaim page.
 */
import { SaveGiftButton } from "./SaveGiftButton";
import { TogetherInvite } from "../../components/together/TogetherInvite";
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Sun, Play, Pause, Loader2, Sparkles, Download } from "lucide-react";
import SoulumoWord from "../../components/SoulumoWord";
import InstallOrb from "../../components/InstallOrb";

import { GiftVoiceNote } from "../../components/soulumoCreate/GiftVoice";
import { GiftThanksRecorder } from "../../components/soulumoCreate/GiftThanks";
const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicShareAudio() {
  const { slug } = useParams();
  const gift = new URLSearchParams(window.location.search);
  const soulumo = gift.get("b") === "soulumo" || window.location.hostname.includes("soulumo");
  const BRAND = soulumo ? "Soulumo" : "Reclaim";
  const pFor = (gift.get("for") || "").slice(0, 40), pFrom = (gift.get("from") || "").slice(0, 40), pNote = (gift.get("m") || "").slice(0, 300);
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const [info, setInfo] = useState(null);
  const giftFor = pFor || info?.gift?.for || "", giftFrom = pFrom || info?.gift?.from || "", giftNote = pNote || info?.gift?.note || "";
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [finished, setFinished] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/public/share/${slug}/info`)
      .then((r) => {
        if (r.status === 404) { setNotFound(true); return null; }
        return r.ok ? r.json() : null;
      })
      .then(setInfo)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setProgress(a.duration ? a.currentTime / a.duration : 0);
    const onEnd = () => { setPlaying(false); setProgress(1); setFinished(true); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, [info]);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { tellSender(); audioRef.current.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  const totalSec = info?.duration_seconds || 0;
  const elapsed = Math.floor(progress * totalSec);
  const fmt = (s) => {
    s = Math.max(0, Math.round(s));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  const isSoulumo = info?.brand === "soulumo" || soulumo;
  const noteId = (gift.get("v") || "").replace(/[^a-f0-9]/g, "").slice(0, 12);
  const told = useRef(false);
  const tellSender = () => {
    if (told.current || (!giftFor && !noteId)) return;
    told.current = true;
    const tok = localStorage.getItem("reclaim_token");
    fetch(`${API}/api/public/share/${slug}/opened`, { method: "POST", headers: { "Content-Type": "application/json", ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
      body: JSON.stringify({ to: giftFor || "", note_id: noteId || "" }) }).catch(() => {});
  };
  const voiceNote = noteId && info ? <GiftVoiceNote slug={slug} noteId={noteId} from={giftFrom} onStart={tellSender} /> : null;
  const thanks = info && (giftFor || noteId) ? <GiftThanksRecorder slug={slug} to={giftFor} noteId={noteId} from={giftFrom} nudge={finished} gold={isSoulumo ? "#e0a97a" : "#fcd34d"} cream={isSoulumo ? "#f5ecd9" : "#fffbeb"} /> : null;

  // ── NOT FOUND ────────────────────────────────────────────────
  if (notFound) {
    if (isSoulumo) {
      return <SoulumoNotFound onGo={() => navigate("/soulumo")} />;
    }
    return (
      <div className="min-h-screen bg-[#0B0805] text-white flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-amber-200/70 text-xs uppercase tracking-[0.3em] mb-2">Reclaim</p>
          <h1 className="text-3xl font-light text-white/85" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            This link is no longer active.
          </h1>
          <button onClick={() => navigate("/")} className="mt-6 px-5 py-2.5 rounded-full bg-amber-400/90 text-[#0B0805] text-sm font-medium">
            Visit Reclaim
          </button>
        </div>
      </div>
    );
  }

  // ── SOULUMO SKIN ────────────────────────────────────────────
  if (isSoulumo) {
    return (
      <SoulumoShareSkin
        info={info}
        loading={loading}
        playing={playing}
        progress={progress}
        totalSec={totalSec}
        elapsed={elapsed}
        fmt={fmt}
        onToggle={toggle}
        audioRef={audioRef}
        slug={slug}
        onExplore={() => navigate("/soulumo")}
        voiceNote={voiceNote}
        thanks={thanks}
        gift={{ to: giftFor, from: giftFrom, note: giftNote }}
      />
    );
  }

  // ── DEFAULT (RECLAIM) SKIN ─────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0805] via-[#100B07] to-[#080604] text-white flex flex-col">
      <header className="px-5 pt-6 pb-4 flex items-center justify-center">
        <div className="flex items-center gap-2 text-amber-200/85">
          <Sun size={18} />
          <span className="text-[11px] uppercase tracking-[0.3em]">{BRAND}</span>
        </div>
      </header>

      <main className="flex-1 px-5 py-8 max-w-md w-full mx-auto flex flex-col">
        <div className="text-center mb-8 mt-4">
          <p className="text-amber-200/70 text-[10px] uppercase tracking-[0.32em] mb-3" data-testid="gift-for">{giftFor ? `A gift for ${giftFor}` : "A gift made for you"}</p>
          <h1 className="text-4xl sm:text-5xl text-amber-50/95 font-light leading-tight" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            {info?.title || "Your Audio"}
          </h1>
          {info?.subtitle && (
            <p className="text-amber-100/55 text-sm mt-3 italic" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
              {info.subtitle}
            </p>
          )}
          {giftNote && (
            <div className="mt-6 rounded-2xl px-5 py-4 text-left border border-amber-200/20 bg-amber-100/[0.04]" data-testid="gift-note">
              <p className="text-amber-50/85 text-lg italic leading-relaxed" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>“{giftNote}”</p>
              {giftFrom && <p className="mt-2 text-amber-200/60 text-xs tracking-wide">— {giftFrom}</p>}
            </div>
          )}
          {voiceNote && <div className="mt-6">{voiceNote}</div>}
          {info?.byline && (
            <p className="text-white/55 text-sm mt-4 italic max-w-xs mx-auto leading-relaxed" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
              {soulumo ? info.byline.replace("Reclaim", "Soulumo") : info.byline}
            </p>
          )}
        </div>

        <div className="rounded-3xl bg-gradient-to-br from-amber-500/15 via-yellow-500/8 to-transparent ring-1 ring-amber-400/25 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-white/45">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : !info ? (
            <p className="text-center text-white/55 text-sm py-8">This audio is not available.</p>
          ) : (
            <>
              <div className="flex items-center gap-4">
                <button
                  onClick={toggle}
                  data-testid="share-play"
                  className={`w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/15 transition ${
                    playing ? "bg-amber-300" : "bg-amber-400/95 hover:bg-amber-300"
                  } text-[#0B0805]`}
                >
                  {playing ? <Pause size={22} /> : <Play size={22} className="translate-x-[2px]" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-amber-100 text-sm tracking-wide font-medium truncate">{info.title}</p>
                  {info.subtitle && (
                    <p className="text-white/45 text-[11px] mt-0.5 italic truncate">{info.subtitle}</p>
                  )}
                </div>
              </div>
              <div className="mt-5">
                <div className="h-1.5 rounded-full bg-white/[0.08] overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-300 to-amber-500 transition-all"
                    style={{ width: `${Math.min(100, progress * 100)}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-1.5 text-[10px] text-white/50 tabular-nums">
                  <span>{fmt(elapsed)}</span>
                  <span>{fmt(totalSec)}</span>
                </div>
              </div>
              <audio
                ref={audioRef}
                src={`${API}${info.audio_url}`}
                preload="metadata"
                data-testid="share-audio"
              />
              <a
                href={`${API}${info.audio_url}?download=1`}
                download={`${slug}.mp3`}
                data-testid="share-download"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/[0.05] hover:bg-white/[0.10] ring-1 ring-amber-300/30 hover:ring-amber-200/55 text-amber-100 text-sm font-medium transition"
              >
                <Download size={16} />
                Download to your phone
              </a>
              <p className="text-white/40 text-[10px] italic mt-2 text-center">
                Plays offline. No account needed. Tap once, keep forever.
              </p>
            </>
          )}
        </div>
        {thanks}

        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-1.5 text-amber-200/70 text-[10px] uppercase tracking-[0.3em] mb-3">
            <Sparkles size={11} />
            <span>What is {BRAND}?</span>
          </div>
          <p className="text-white/65 text-sm leading-relaxed mb-5 max-w-xs mx-auto">
            {soulumo ? "Soulumo turns the words you want to live by into a personal audio installation, in your own voice, for while you rest."
              : "Reclaim is a daily rhythm for the mind, body and nervous system. Audios, rituals and resets — for people who want to live consciously."}
          </p>
          <button
            onClick={() => (soulumo ? window.location.assign("/soulumo") : navigate("/"))}
            data-testid="share-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400/95 hover:bg-amber-300 text-[#0B0805] text-sm font-medium transition"
          >
            Explore {BRAND}
            <span className="text-base">→</span>
          </button>
        </div>

        <div className="flex-1" />

        <footer className="text-center text-white/30 text-[10px] uppercase tracking-[0.25em] py-6">
          {soulumo ? "Soulumo" : "Reclaim · School of Consciousness"}
        </footer>
      </main>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Soulumo skin — dark navy + gold, matching soulumo.app hero
// ─────────────────────────────────────────────────────────────

const S = {
  bg: "radial-gradient(ellipse at 50% 12%, #131f38 0%, #0a132a 42%, #030710 88%)",
  cream: "#f5ecd9",
  creamDim: "rgba(245,236,217,0.72)",
  creamMist: "rgba(245,236,217,0.42)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.86)",
  goldMist: "rgba(224,169,122,0.22)",
  ink: "#0a132a",
};

function SoulumoShareSkin({
  info, loading, playing, progress, totalSec, elapsed, fmt,
  onToggle, audioRef, slug, onExplore, voiceNote, thanks, gift,
}) {
  const togetherId = (new URLSearchParams(window.location.search).get("t") || "").replace(/[^a-z0-9_]/g, "").slice(0, 20);
  return (
    <div
      className="min-h-screen relative overflow-x-hidden flex flex-col"
      style={{ background: S.bg, color: S.cream }}
      data-testid="soulumo-share-page"
    >
      {/* Breathing halo */}
      <div
        className="pointer-events-none absolute"
        style={{
          top: "-14%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 560,
          height: 560,
          background:
            "radial-gradient(circle, rgba(224,169,122,0.24) 0%, rgba(224,169,122,0.06) 30%, transparent 65%)",
          filter: "blur(30px)",
        }}
        aria-hidden
      />

      <header className="relative z-10 pt-6 pb-2 flex items-center justify-center">
        <SoulumoWord size="xs" />
      </header>

      <main className="relative z-10 flex-1 px-5 pt-6 pb-10 max-w-md w-full mx-auto flex flex-col">
        {/* Hero */}
        <div className="text-center mb-8">
          <div style={{ width: 132, height: 132, margin: "0 auto" }}>
            <InstallOrb state="resting" size={132} />
          </div>
          <p
            className="mt-5 text-[0.58rem] uppercase tracking-[0.4em]"
            style={{ color: S.goldSoft, fontFamily: '"Manrope", sans-serif' }}
          >
            {gift?.to ? `· A gift for ${gift.to} ·` : "· A private installation ·"}
          </p>
          <h1
            className="mt-4 font-light leading-tight"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(1.75rem, 6vw, 2.4rem)",
              color: S.cream,
              letterSpacing: "-0.005em",
            }}
            data-testid="soulumo-share-title"
          >
            {info?.title || "Your Installation"}
          </h1>
          {info?.subtitle && (
            <p
              className="mt-3 italic"
              style={{ color: S.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "0.98rem" }}
            >
              {info.subtitle}
            </p>
          )}
          {info?.byline && (
            <p
              className="mt-4 italic max-w-xs mx-auto leading-relaxed"
              style={{ color: S.creamMist, fontFamily: '"Cormorant Garamond", serif', fontSize: "0.9rem" }}
            >
              {info.byline.replace("Reclaim", "Soulumo")}
            </p>
          )}
        </div>

        {gift?.note && (
          <p className="-mt-4 mb-6 text-center italic text-lg leading-relaxed" style={{ color: S.cream, fontFamily: '"Cormorant Garamond", serif' }} data-testid="soulumo-share-gift-note">
            “{gift.note}”{gift.from ? <span className="block mt-1 text-sm not-italic" style={{ color: S.goldSoft }}>— {gift.from}</span> : null}
          </p>
        )}
        {voiceNote}
        {/* Player */}
        <div
          className="rounded-3xl p-6"
          style={{
            background: "rgba(224,169,122,0.06)",
            border: `1px solid ${S.goldMist}`,
          }}
          data-testid="soulumo-share-player"
        >
          {loading ? (
            <div className="flex items-center justify-center py-10" style={{ color: S.creamMist }}>
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : !info ? (
            <p className="text-center py-8 italic" style={{ color: S.creamMist, fontFamily: '"Cormorant Garamond", serif' }}>
              This installation is not available.
            </p>
          ) : (
            <>
              <div className="flex items-center gap-4">
                <button
                  onClick={onToggle}
                  data-testid="share-play"
                  className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 transition"
                  style={{
                    background: S.gold,
                    color: S.ink,
                    boxShadow: "0 8px 26px rgba(224,169,122,0.28)",
                  }}
                  aria-label={playing ? "Pause" : "Play"}
                >
                  {playing ? <Pause size={22} /> : <Play size={22} className="translate-x-[2px]" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p
                    className="tracking-wide truncate"
                    style={{ color: S.cream, fontFamily: '"Manrope", sans-serif', fontSize: "0.88rem" }}
                  >
                    {info.title}
                  </p>
                  <p
                    className="mt-0.5 italic"
                    style={{ color: S.creamMist, fontFamily: '"Cormorant Garamond", serif', fontSize: "0.78rem" }}
                  >
                    {playing ? "Playing" : "Ready"} · {fmt(totalSec)}
                  </p>
                </div>
              </div>

              {/* Progress */}
              <div className="mt-5">
                <div
                  className="h-1.5 rounded-full overflow-hidden"
                  style={{ background: "rgba(245,236,217,0.08)" }}
                >
                  <div
                    className="h-full transition-all"
                    style={{
                      width: `${Math.min(100, progress * 100)}%`,
                      background: `linear-gradient(90deg, ${S.gold} 0%, #f4c69f 100%)`,
                    }}
                  />
                </div>
                <div
                  className="flex items-center justify-between mt-1.5 tabular-nums"
                  style={{ color: S.creamMist, fontFamily: '"Manrope", sans-serif', fontSize: "0.66rem" }}
                >
                  <span>{fmt(elapsed)}</span>
                  <span>{fmt(totalSec)}</span>
                </div>
              </div>

              <audio
                ref={audioRef}
                src={`${API}${info.audio_url}`}
                preload="metadata"
                data-testid="share-audio"
              />

              {togetherId ? <TogetherInvite id={togetherId} slug={slug} /> : info.gift && <SaveGiftButton slug={slug} />}
              <a
                href={`${API}${info.audio_url}?download=1`}
                download={`${slug}.mp3`}
                data-testid="share-download"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 transition"
                style={{
                  background: "rgba(245,236,217,0.04)",
                  border: `1px solid ${S.goldMist}`,
                  color: S.cream,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.72rem",
                  letterSpacing: "0.26em",
                  textTransform: "uppercase",
                }}
              >
                <Download size={14} />
                Download to your phone
              </a>
              <p
                className="mt-3 text-center italic"
                style={{ color: S.creamMist, fontFamily: '"Cormorant Garamond", serif', fontSize: "0.76rem" }}
              >
                Plays offline. No account needed. Tap once, keep forever.
              </p>
            </>
          )}
        </div>
        {thanks}

        {/* What is Soulumo */}
        <div className="mt-14 text-center">
          <div
            className="text-[0.58rem] uppercase tracking-[0.4em] mb-3"
            style={{ color: S.goldSoft, fontFamily: '"Manrope", sans-serif' }}
          >
            · What is Soulumo ·
          </div>
          <p
            className="mx-auto max-w-xs leading-relaxed italic"
            style={{
              color: S.creamDim,
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "1rem",
            }}
          >
            Soulumo helps you re-architect your inner world — in your own voice. Personalised audio
            installations of your words, layered with music and frequency.
          </p>
          <button
            onClick={onExplore}
            data-testid="share-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-full px-7 py-3 transition"
            style={{
              background: "rgba(224,169,122,0.22)",
              border: `1px solid ${S.gold}`,
              color: S.cream,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.72rem",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
            }}
          >
            Explore Soulumo <span style={{ fontSize: "0.9rem", lineHeight: 1 }}>→</span>
          </button>
        </div>

        <div className="flex-1" />

        <footer
          className="text-center py-8"
          style={{
            color: S.creamMist,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.55rem",
            letterSpacing: "0.32em",
            textTransform: "uppercase",
          }}
        >
          Soulumo · soulumo.app
        </footer>
      </main>
    </div>
  );
}

function SoulumoNotFound({ onGo }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{ background: S.bg, color: S.cream }}
      data-testid="soulumo-share-not-found"
    >
      <div className="text-center">
        <SoulumoWord size="sm" />
        <h1
          className="mt-6 font-light"
          style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "clamp(1.6rem, 5vw, 2rem)" }}
        >
          This installation is no longer active.
        </h1>
        <button
          onClick={onGo}
          className="mt-8 rounded-full px-7 py-3"
          style={{
            background: "rgba(224,169,122,0.22)",
            border: `1px solid ${S.gold}`,
            color: S.cream,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.72rem",
            letterSpacing: "0.28em",
            textTransform: "uppercase",
          }}
        >
          Visit Soulumo →
        </button>
      </div>
    </div>
  );
}
